import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { ContractGenerationTemplateInDb } from '@/api';
import {
  TemplatesService,
  type TemplateSearchQuery,
} from '@/services/contractGeneration/templatesService';

export const useContractGenerationStore = defineStore('contractGeneration', () => {
  const templates = ref<ContractGenerationTemplateInDb[]>([]);
  const totalCount = ref<number>(0);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);

  async function fetchTemplates(query: TemplateSearchQuery = {}): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const result = await TemplatesService.search(query);
      templates.value = result.results;
      totalCount.value = result.result_count;
    } catch (error_) {
      error.value = error_ instanceof Error ? error_.message : 'Failed to fetch templates';
      throw error_;
    } finally {
      loading.value = false;
    }
  }

  async function deleteTemplate(uid: string): Promise<void> {
    await TemplatesService.delete(uid);
    templates.value = templates.value.filter(template => template.uid !== uid);
    totalCount.value = Math.max(0, totalCount.value - 1);
  }

  function upsertTemplate(template: ContractGenerationTemplateInDb): void {
    const index = templates.value.findIndex(existing => existing.uid === template.uid);
    if (index === -1) {
      templates.value = [template, ...templates.value];
      totalCount.value += 1;
    } else {
      templates.value[index] = template;
    }
  }

  return {
    templates,
    totalCount,
    loading,
    error,
    fetchTemplates,
    deleteTemplate,
    upsertTemplate,
  };
});
